import { generatePath, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { PaginationResponseType } from 'app/console-api';
import {
  Button,
  FlexColumn,
  FlexRow,
  SvgIcon,
  TableRow,
  TableRowType
} from '@make-software/cspr-design';
import styled from 'styled-components';
import {
  APPS_STATISTICS_PATH,
  APPS_TASKS_CREATE_PATH,
  APPS_TASKS_EDIT_PATH,
  APPS_TASKS_VIEW_PATH,
  SIGN_IN_PATH
} from 'app/route/paths';
import NoFound from 'assets/logos/no-found.svg';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import Table from 'app/components/common/table/table';
import { ErrorResult, Pagination } from 'app/organizations-api/types';
import { ExtendedTasksResponse, getExtendedTasks } from 'app/console-api/tasks/tasks-extended';
import { HttpError } from 'app/components/utils/http-error';
import { customErrorMessage, statusCode } from 'app/constants';
import { TaskListItem } from 'app/components/management/apps/tasks/list/task-list-item';
import { StyledTableFiltersBlock, TablePageContainer } from 'app/components/common/common-styles';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { TaskTypeAdapter, TaskTypeFilter } from 'app/components/filters/task-type-filter';
import { AppCategoriesFilter, CategoriesAdapter } from 'app/components/filters/categories-filter';
import { IsActiveAdapter } from 'app/components/filters/is-active-filter';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import { EntityType } from 'app/console-api/plugins/plugins';
import { TaskStatusFilter } from 'app/components/filters/task-status-filter';

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: '120px',
    padding: ['0', 'unset', 'unset']
  })
);

export const TasksListPage = () => {
  const { appID: appID } = useParams<{ appID: string }>();
  const navigate = useNavigate();
  const filterController = useUrlFilterController();
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 5 });
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [tasksList, setTasksList] = useState<PaginationResponseType<ExtendedTasksResponse> | null>(
    null
  );
  const lastFetchedFiltersKeyRef = React.useRef<string | null>(null);

  useEffect(() => {
    setLoading(true);
    if (!appID) {
      return;
    }
    const filtersKey = filterController.buildCacheKey([
      IsActiveAdapter,
      TaskTypeAdapter,
      CategoriesAdapter
    ]);
    if (lastFetchedFiltersKeyRef.current !== filtersKey) {
      setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }));
      pagination.currentPage = 1;
    }
    lastFetchedFiltersKeyRef.current = filtersKey;

    setUnexpectedError(null);
    getExtendedTasks(appID, filterController, pagination)
      .then((response) => {
        setTasksList(response);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [appID, pagination, filterController]);

  const createButton = () => {
    navigate(generatePath(APPS_TASKS_CREATE_PATH, { appID: appID || '' }));
  };

  const viewTask = (data: ExtendedTasksResponse) => {
    navigate(generatePath(APPS_TASKS_VIEW_PATH, { appID: appID || '', id: data.task.id }));
  };

  const editTask = (data: ExtendedTasksResponse) => {
    navigate(generatePath(APPS_TASKS_EDIT_PATH, { appID: appID || '', id: data.task.id }));
  };

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize != limit) {
      page = 1;
    }
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: limit
    }));
  };

  const moreStatisticsLink = (id: string) => {
    navigate(
      generatePath(APPS_STATISTICS_PATH, { appID: appID || '' }) +
        '?' +
        new URLSearchParams({ tasks: id }).toString()
    );
  };

  const noTasks = !tasksList?.data?.length && !loading && !unexpectedError;

  return (
    <TablePageContainer>
      <FlexColumn>
        <FlexRow justify={'space-between'} align={'center'} style={{ marginRight: '12px' }}>
          <StyledTableFiltersBlock>
            {appID ? (
              <>
                <TaskStatusFilter />
                <TaskTypeFilter eventType={EntityType.Task} />
                <AppCategoriesFilter appID={appID}></AppCategoriesFilter>
              </>
            ) : (
              <SkeletonLoader height={'35px'} />
            )}
          </StyledTableFiltersBlock>
          <FlexColumn>
            <StyledButton color="primaryBlue" onClick={createButton}>
              Create new
            </StyledButton>
          </FlexColumn>
        </FlexRow>
        {noTasks ? (
          <PreloadTableWindow
            icon={<SvgIcon src={NoFound} height={120} width={224} />}
            title={'No tasks yet'}
            windowTitle=""
            subtitle="Create one to get started"
            onSubmitTitle="Create new"
            onSubmit={createButton}
          />
        ) : (
          <Table
            loading={loading}
            data={tasksList?.data || []}
            pageCount={tasksList?.page_count || 1}
            error={unexpectedError}
            pageSize={pagination.pageSize}
            currentPage={pagination.currentPage}
            handlePageActions={handlePageActions}
            tableRowType={TableRowType.TextSingleLine}
            renderDataHeader={() => (
              <TableRow>
                <BaseTableHeader fixedWidthRem={25}>Task</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={20}>Details</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={15}></BaseTableHeader>
                <BaseTableHeader fixedWidthRem={20}>Rewarded users</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={5} align="right">
                  Action
                </BaseTableHeader>
              </TableRow>
            )}
            renderPaginatedData={(one) => {
              return one.map((task) => (
                <TaskListItem
                  key={task.task.id}
                  data={task}
                  onView={viewTask}
                  onEdit={editTask}
                  onStatistics={moreStatisticsLink}
                />
              ));
            }}
          />
        )}
      </FlexColumn>
    </TablePageContainer>
  );
};
